import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useRole, useCreateRole, useUpdateRole } from '../../services/httpServices/queries';
import {
  ArrowLeft,
  Loader2,
  AlertCircle,
  Save,
  ToggleLeft,
  ToggleRight,
  Globe,
} from 'lucide-react';
import { cn } from '../../lib/utils';

export function RoleForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;

  const { data: roleData, isLoading: roleLoading, error: roleError } = useRole(id || '');
  const createRole = useCreateRole();
  const updateRole = useUpdateRole();

  const role = roleData?.data;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isActive: true,
    isWebsiteOnly: false,
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isEdit && role) {
      setFormData({
        name: role.name || '',
        description: role.description || '',
        isActive: role.isActive ?? true,
        isWebsiteOnly: role.isWebsiteOnly ?? false,
      });
    }
  }, [isEdit, role]);

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    if (!formData.name.trim()) errors.name = 'Role name is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      if (isEdit && id) {
        await updateRole.mutateAsync({ id, data: formData });
        navigate('/roles');
      } else {
        await createRole.mutateAsync(formData);
        navigate('/roles');
      }
    } catch {
      // handled by mutation
    }
  };

  if (roleLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (roleError) {
    return (
      <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
        <AlertCircle size={18} />
        <span>Failed to load role. Please try again.</span>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 max-w-xl mx-auto">
      {/* Top Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/roles')}
          className="inline-flex items-center gap-2 text-text-muted hover:text-text transition-colors text-sm cursor-pointer"
        >
          <ArrowLeft size={18} />
          Back to Roles
        </button>
      </div>

      {/* Header */}
      <h1 className="text-2xl font-bold text-text">{isEdit ? 'Edit Role' : 'Add Role'}</h1>

      <form onSubmit={handleSubmit} className="bg-surface border border-border rounded-xl p-6 space-y-6">
        <div>
          <label className="block text-sm font-medium text-text mb-1">Role Name *</label>
          <input
            required
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className={cn(
              'w-full px-3 py-2 border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary',
              formErrors.name ? 'border-danger' : 'border-border'
            )}
          />
          {formErrors.name && <p className="text-xs text-danger mt-1">{formErrors.name}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-text mb-1">Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={3}
            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setFormData({ ...formData, isActive: !formData.isActive })}
            className="flex items-center gap-2 text-sm text-text"
          >
            {formData.isActive ? <ToggleRight className="text-success" size={20} /> : <ToggleLeft className="text-text-muted" size={20} />}
            <span>{formData.isActive ? 'Active' : 'Inactive'}</span>
          </button>
        </div>

        <div className="flex items-start gap-2">
          <button
            type="button"
            onClick={() => setFormData({ ...formData, isWebsiteOnly: !formData.isWebsiteOnly })}
            className="flex items-center gap-2 text-sm text-text"
          >
            {formData.isWebsiteOnly ? <ToggleRight className="text-success" size={20} /> : <ToggleLeft className="text-text-muted" size={20} />}
            <span className="inline-flex items-center gap-1.5">
              <Globe size={14} /> Website only
            </span>
          </button>
          <p className="text-xs text-text-muted mt-1">
            Employees assigned this role appear as selectable authors when writing website blog posts.
          </p>
        </div>

        <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
          <button
            type="button"
            onClick={() => navigate('/roles')}
            className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={createRole.isPending || updateRole.isPending}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50"
          >
            {(createRole.isPending || updateRole.isPending) ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            {isEdit ? 'Update Role' : 'Create Role'}
          </button>
        </div>
      </form>
    </div>
  );
}
